<script>
import { formatCityName } from "@/utils/cityName.mjs";

export default {
    name: "PropertyCardNew",

    props: {
        image: { type: String, default: "" },
        // Judul besar = nama unit (DisplayName). `name` dipertahankan sbg fallback lama.
        name: { type: String, default: "" },
        title: { type: String, default: "" },
        // Baris kecil di bawah judul: tipe kamar + properti.
        roomType: { type: String, default: "" },
        property: { type: String, default: "" },
        locationCity: { type: String, default: "" },
        locationProvince: { type: String, default: "" },
        price: { type: [Number, String], default: "" },
        rating: { type: [Number, String], default: 0 },
        reviews: { type: [Number, String], default: "" },
        pax: { type: [Number, String], default: 0 },
        sizeRoom: { type: [Number, String], default: 0 },
        isInitiallyFavorited: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        isAllowDetailRoom: { type: Boolean, default: true },
        // false = properti ada tapi penuh di tanggal pencarian
        isAvailable: { type: Boolean, default: true },
        defaultImage: {
            type: String,
            default: () => "/assets/images/ch-placeholder-new.png",
        },
        showDateAvailable: { type: String, default: "" },
        hasVideo: { type: Boolean, default: false }, // CH-W57 — tampilkan ▶ di gambar
        // Kartu di grid rapat (2 kolom mobile): judul boleh 2 baris + label bawah dipadatkan.
        compact: { type: Boolean, default: false },
    },
    emits: ["play"],
    computed: {
        // Nama kota tanpa awalan "Kota "/"Kota Administrasi ".
        cityLabel() {
            return formatCityName(this.locationCity);
        },
    },
};
</script>

<template>
    <div
        class="flex flex-col overflow-hidden transition duration-200 bg-white border border-gray-200 shadow-sm group rounded-xl hover:-translate-y-1 hover:shadow-lg"
    >
        <!-- Photo -->
        <div class="relative ch-photo">
            <!-- Skeleton foto: div block (height selalu terpakai, tak gepeng di mobile) -->
            <div
                v-if="loading"
                class="w-full bg-gray-200 h-44 animate-pulse"
            ></div>
            <LazyImage
                v-else
                :src="image"
                :alt="title"
                :defaultImage="defaultImage"
                roundedClass="rounded-t-xl"
                class="object-cover w-full h-44"
            />
            <!-- Badge penuh di tanggal pencarian -->
            <span
                v-if="!loading && !isAvailable"
                class="absolute z-10 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border rounded-md top-2 left-2 bg-amber-100 text-amber-700 border-amber-300"
            >
                <AppIcon icon="mdi:calendar-remove-outline" class="w-3.5 h-3.5" />
                {{ $t("unavailableOnDates") }}
            </span>
            <div
                v-if="$slots.favoriteIcon"
                class="absolute z-10 top-2 right-2"
            >
                <slot name="favoriteIcon" />
            </div>
            <!-- CH-W57 — klik gambar → play reels (stop propagate agar tak buka detail) -->
            <button
                v-if="hasVideo && !loading"
                type="button"
                class="absolute inset-0 z-[5] flex items-center justify-center group/play"
                aria-label="Play video"
                @click.stop="$emit('play')"
            >
                <span class="flex items-center justify-center text-white transition rounded-full w-12 h-12 bg-black/45 group-hover/play:bg-black/70">
                    <AppIcon icon="mdi:play" class="w-6 h-6" />
                </span>
            </button>
        </div>

        <!-- Body -->
        <div
            :class="[
                'flex flex-col flex-1',
                compact ? 'gap-1 px-3 pt-2 pb-3' : 'gap-1.5 px-4 pt-3 pb-4',
            ]"
        >
            <template v-if="loading">
                <div class="w-3/4 h-4 rounded bg-gray-200 animate-pulse"></div>
                <div class="w-2/5 h-3 rounded bg-gray-200 animate-pulse"></div>
                <div class="w-3/5 h-3 rounded bg-gray-200 animate-pulse"></div>
                <div class="flex gap-4 pt-3 mt-1 border-t border-gray-100 border-dashed">
                    <div class="w-16 h-3 rounded bg-gray-200 animate-pulse"></div>
                    <div class="w-16 h-3 rounded bg-gray-200 animate-pulse"></div>
                </div>
                <div class="flex items-center justify-between mt-2">
                    <div class="w-24 h-5 rounded bg-gray-200 animate-pulse"></div>
                    <div class="w-20 h-4 rounded bg-gray-200 animate-pulse"></div>
                </div>
            </template>

            <template v-else>
                <!-- Judul besar: nama unit (DisplayName). Fallback ke `name` (pemakai lama).
                     Compact (2 kolom) → boleh 2 baris agar nama tak terpotong. -->
                <h3
                    :title="title || name"
                    :class="[
                        'font-semibold leading-snug text-new-900',
                        compact ? 'text-sm line-clamp-2' : 'text-base truncate',
                    ]"
                >
                    {{ title || name }}
                </h3>

                <!-- Baris kecil: tipe kamar + properti. Satu baris bila muat;
                     pisah baris otomatis (flex-wrap) kalau tidak muat. -->
                <p
                    v-if="roomType || property"
                    :title="[roomType, property].filter(Boolean).join(' · ')"
                    :class="[
                        'flex flex-wrap items-center gap-x-1.5 font-medium text-gray-500',
                        compact ? 'text-[11px]' : 'text-xs',
                    ]"
                >
                    <span v-if="roomType">{{ roomType }}</span>
                    <span v-if="roomType && property" class="text-gray-300">·</span>
                    <span v-if="property">{{ property }}</span>
                </p>

                <!-- Lokasi: kota saja (provinsi tidak ditampilkan) -->
                <p
                    :class="[
                        'flex items-center gap-1 text-gray-500',
                        compact ? 'text-[11px]' : 'text-xs',
                    ]"
                >
                    <AppIcon
                        icon="mdi:map-marker-outline"
                        class="flex-none w-3.5 h-3.5 text-gray-400"
                    />
                    <span class="truncate">{{ cityLabel }}</span>
                </p>

                <!-- Meta -->
                <div
                    v-if="isAllowDetailRoom"
                    :class="[
                        'flex flex-wrap items-center text-[11px] text-gray-500 border-t border-gray-100 border-dashed gap-x-3 gap-y-1',
                        compact ? 'pt-1.5 mt-0.5' : 'pt-2 mt-1',
                    ]"
                >
                    <span class="inline-flex items-center gap-1 whitespace-nowrap">
                        <AppIcon
                            icon="mdi:account-outline"
                            class="flex-none w-4 h-4 text-gray-400"
                        />
                        {{ pax }} Guests
                    </span>
                    <span class="inline-flex items-center gap-1 whitespace-nowrap">
                        <AppIcon
                            icon="mdi:ruler-square"
                            class="flex-none w-4 h-4 text-gray-400"
                        />
                        {{ sizeRoom }} m²
                    </span>
                </div>

                <!-- Footer: harga (penekanan) + aksi ikon -->
                <div
                    :class="[
                        'flex items-center justify-between gap-2',
                        compact ? 'mt-1' : 'mt-2',
                    ]"
                >
                    <span
                        class="flex-1 min-w-0 text-xs font-bold truncate sm:text-sm md:text-base tabular-nums text-new-900"
                    >
                        Rp {{ $formatters.formatDecimal(price) }}
                    </span>
                    <span
                        v-if="isAvailable"
                        title="View Details"
                        class="inline-flex items-center justify-center flex-none rounded-full w-8 h-8 text-secondary bg-secondary/10 group-hover:bg-secondary/20"
                    >
                        <i
                            class="text-sm transition-transform duration-200 fa-solid fa-arrow-right-long group-hover:translate-x-0.5"
                        ></i>
                    </span>
                    <span
                        v-else
                        class="flex-none text-[11px] font-semibold text-amber-700 whitespace-nowrap"
                    >
                        {{ $t("seeOtherDates") }}
                    </span>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped>
/* LazyImage merender gambar placeholder/fallback dengan `object-contain bg-white`,
   yang menyisakan pita putih (letterbox) saat rasio gambar != rasio frame.
   Paksa cover HANYA di dalam kartu ini supaya foto selalu mengisi penuh. */
.ch-photo :deep(img) {
    object-fit: cover !important;
    background: transparent !important;
}
</style>
